import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NotificationResponseDto, Notification, UserInteractionStatusEnum } from '../../commonTypes/NotificationTypes/notification.types';
import { RootState } from '..';

export interface NotificationCenterState {
    notifications: NotificationResponseDto;
    hasUnreadNotifications: boolean;
}

const initialState: NotificationCenterState = {
    notifications: {
        items: [],
        total: 0,
    },
    hasUnreadNotifications: false,
};

const notificationCenterSlice = createSlice({
    name: 'notificationCenter',
    initialState,
    reducers: {
        setNotifications: (state, action: PayloadAction<NotificationResponseDto>) => {
            state.notifications = {
                ...action.payload,
                items: [...action.payload.items],
            };
            state.hasUnreadNotifications = action.payload.items.some(
                notification => notification.userInteractionStatus === UserInteractionStatusEnum.UNREAD
            );
        },
        addNotification: (state, action: PayloadAction<Notification>) => {
            state.notifications = {
                ...state.notifications,
                items: [...state.notifications.items, action.payload],
            };
            state.hasUnreadNotifications = true;
        },
        markNotificationAsRead: (state, action: PayloadAction<string>) => {
            const notification = state.notifications.items.find(item => item.id === action.payload);
            if (notification) {
                notification.userInteractionStatus = UserInteractionStatusEnum.READ;
            }
            state.hasUnreadNotifications = state.notifications.items.some(
                notification => notification.userInteractionStatus === UserInteractionStatusEnum.UNREAD
            );
        },
        markAllNotificationsAsRead: state => {
            state.notifications.items.forEach(notification => {
                if (notification.userInteractionStatus === UserInteractionStatusEnum.UNREAD) {
                    notification.userInteractionStatus = UserInteractionStatusEnum.READ;
                }
            });
            state.hasUnreadNotifications = false;
        },
        setHasUnreadNotifications: (state, action: PayloadAction<boolean>) => {
            state.hasUnreadNotifications = action.payload;
        },
    },
});

export const { setNotifications, addNotification, markNotificationAsRead, markAllNotificationsAsRead, setHasUnreadNotifications } =
    notificationCenterSlice.actions;

export const selectNotifications = (state: RootState) => state.notificationCenter.notifications;
export const selectHasUnreadNotifications = (state: RootState) => state.notificationCenter.hasUnreadNotifications;
export const selectNotificationById = (id: string) => (state: RootState) =>
    state.notificationCenter.notifications.items.find(notification => notification.id === id);
export const selectUnreadNotifications = (state: RootState) =>
    state.notificationCenter.notifications.items.filter(notification => notification.userInteractionStatus === UserInteractionStatusEnum.UNREAD);
export const selectNotificationsByStatus = (status: UserInteractionStatusEnum) => (state: RootState) =>
    state.notificationCenter.notifications.items.filter(notification => notification.userInteractionStatus === status);

export default notificationCenterSlice.reducer;
